import { useState, useRef } from "react";
import { Link } from "wouter";
import { Loader2, MailCheck } from "lucide-react";
import LogoAnimation from "@/components/LogoAnimation";
// v7.05 — restyled to the admoji.com/ads look, matching login + signup.
import {
  INK, MUTED, PINK, LINE,
  pageStyle, cardStyle, monoLabelStyle, h1Style, serifStyle, subStyle,
  labelStyle, fieldStyle, buttonStyle, errorStyle, footerStyle,
  fieldFocus, fieldBlur, btnHoverOn, btnHoverOff,
} from "./auth-theme";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting.current) return;
    setError("");
    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    submitting.current = true;
    setLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Something went wrong. Try again.");
        return;
      }
      setSent(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      submitting.current = false;
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={pageStyle}
      data-testid="forgot-password-page"
    >
      <div className="w-full" style={{ maxWidth: 400 }}>
        <div className="flex justify-center mb-7">
          <LogoAnimation size={126} />
        </div>

        <div style={cardStyle}>
          {sent ? (
            <div data-testid="forgot-sent">
              <MailCheck className="h-9 w-9 mb-4" style={{ color: PINK }} />
              <h1 style={h1Style}>
                Check your{" "}
                <span style={serifStyle}>email.</span>
              </h1>
              <p style={{ ...subStyle, lineHeight: 1.55 }}>
                If an account exists for <span style={{ color: INK, fontWeight: 500 }}>{email.trim()}</span>, a
                password reset link is on its way. It expires in 1 hour.
              </p>
              <Link
                href="/login"
                className="inline-block mt-6"
                style={{ fontSize: 12.5, fontWeight: 600, color: PINK }}
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <div style={monoLabelStyle}>Ad Manager</div>
                <h1 style={h1Style}>
                  Reset your{" "}
                  <span style={serifStyle}>password.</span>
                </h1>
                <p style={subStyle}>
                  Enter your email and we'll send you a link to choose a new one.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="text-sm px-4 py-3" style={errorStyle} data-testid="forgot-error">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block mb-1.5" style={labelStyle}>
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    autoComplete="email"
                    autoFocus
                    data-testid="input-email"
                    className="w-full h-11 px-3 outline-none transition-all"
                    style={fieldStyle}
                    onFocus={fieldFocus}
                    onBlur={fieldBlur}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="button-send-reset"
                  className="w-full flex items-center justify-center transition-colors disabled:opacity-60"
                  style={buttonStyle}
                  onMouseEnter={(e) => btnHoverOn(e, loading)}
                  onMouseLeave={btnHoverOff}
                >
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : "Send reset link"}
                </button>

                <div className="flex items-center gap-3 pt-1">
                  <div className="h-px flex-1" style={{ backgroundColor: LINE }} />
                </div>

                <p className="text-center" style={{ fontSize: 12.5, color: MUTED }}>
                  Remembered it?{" "}
                  <Link href="/login" style={{ fontWeight: 600, color: PINK }}>Sign in</Link>
                </p>
              </form>
            </>
          )}
        </div>

        <p className="text-center mt-6" style={footerStyle}>Admoji Ad Manager</p>
      </div>
    </div>
  );
}
